import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Stack,
  Dropdown,
  IDropdownOption,
  IDropdownProps,
  Label,
  Icon,
  TooltipHost,
  ITooltipHostStyles
} from "@fluentui/react";

import { DismissRegular } from "@fluentui/react-icons";

import {
  OpenAiModel,
  SearchIndex
} from "../../api";
import {
  setPreferredAiModel,
  getPreferredAiModel,
  setPreferredResponseMode,
  getPreferredResponseMode,
  setPreferredSearchIndex,
  getPreferredSearchIndex
} from "../../components/Utils/utils";

import styles from "./SettingsPanel.module.css";

export type SettingsPanelProps = {
  setIsSettingsPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  availableModels: OpenAiModel[];
  availableIndexes: SearchIndex[];
};

export const SettingsPanel: React.FC<SettingsPanelProps> = (props) => {
  const { setIsSettingsPanelOpen } = props;
  const { availableModels } = props;
  const { availableIndexes } = props;

  const [selectedModel, setSelectedModel] = useState<IDropdownOption | undefined>();
  const [availableModelOptions, setAvailableModelOptions] = useState<IDropdownOption[]>([]);

  const [selectedIndex, setSelectedIndex] = useState<IDropdownOption | undefined>();
  const [availableIndexOptions, setAvailableIndexOptions] = useState<IDropdownOption[]>([]);

  const [selectedMode, setSelectedMode] = useState<IDropdownOption | undefined>();

  const calloutProps = { gapSpace: 0 };
  const hostStyles: Partial<ITooltipHostStyles> = { root: { display: 'inline-block' } };

  const modeOptions: IDropdownOption[] = [
    { key: "simple", text: "Simple" },
    { key: "standard", text: "Standard" },
    { key: "enhanced", text: "Enhanced" }
  ];

  // Handler for model selection
  const onSelectedModel = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    //console.log(option);
    if (option) {
      setSelectedModel(option);
      // Store the selected model in a cookie
      setPreferredAiModel(option.key as string);
    }
  };

  // Helper function to restore selected model from cookie
  const restoreSelectedModelFromCookie = (options: IDropdownOption[]) => {
    const savedModelKey = getPreferredAiModel();
    if (savedModelKey && options.length > 0) {
      const savedModel = options.find(option => option.key === savedModelKey);
      if (savedModel) {
        setSelectedModel(savedModel);
        return;
      }
    }

    // Fall back to first option if no saved model or saved model not found
    setSelectedModel(options[0]);
  };

  useEffect(() => {
    const options: IDropdownOption[] = availableModels.map((model) => {
      const option: IDropdownOption = {
        key: model.deploymentName,
        text: model.displayName
      };
      return option;
    });

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAvailableModelOptions(options);

    // Restore selected model from cookie or use first option
    restoreSelectedModelFromCookie(options);
  }, [availableModels])

  // Handler for search index selection
  const onSelectedIndex = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    if (option) {
      setSelectedIndex(option);
      setPreferredSearchIndex(option.key as string);
    }
  };

  // Helper function to restore selected index from cookie
  const restoreSelectedIndexFromCookie = (options: IDropdownOption[]) => {
    const savedKey = getPreferredSearchIndex();
    if (savedKey && options.length > 0) {
      const saved = options.find(option => option.key === savedKey);
      if (saved) {
        setSelectedIndex(saved);
        return;
      }
    }
    setSelectedIndex(options[0]);
  };

  useEffect(() => {
    const options: IDropdownOption[] = availableIndexes.map((idx) => ({
      key: idx.id,
      text: idx.displayName
    }));

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAvailableIndexOptions(options);
    restoreSelectedIndexFromCookie(options);
  }, [availableIndexes])

  // Helper function to restore selected mode from cookie
  const restoreSelectedModeFromCookie = (options: IDropdownOption[]) => {
    const savedModelKey = getPreferredResponseMode();
    if (savedModelKey && options.length > 0) {
      const savedModel = options.find(option => option.key === savedModelKey);
      if (savedModel) {
        setSelectedMode(savedModel);
        return;
      }
    }

    // Fall back to first option if no saved model or saved model not found
    setSelectedMode(options[0]);
  };

  useEffect(() => {
    restoreSelectedModeFromCookie(modeOptions); // eslint-disable-line react-hooks/set-state-in-effect
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handler for mode selection
  const onSelectedMode = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    //console.log(option);
    if (option) {
      setSelectedMode(option);
      // Store the selected model in a cookie
      setPreferredResponseMode(option.key as string);
    }
  };
  
  const onRenderPreferredAiModelLabel = (labelProps?: IDropdownProps): JSX.Element => {
    return (
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8, padding: 0 }}>
        <Label>{labelProps?.label}</Label>
        <TooltipHost
          content="This setting will be used for every message you send."
          closeDelay={100}
          calloutProps={calloutProps}
          styles={hostStyles}
        >
          <Icon
            iconName="Info"
            aria-label="Info"
            className={styles.iconInfo}
          />
        </TooltipHost>
      </Stack>
    );
  };

  const onRenderResponseModeLabel = (labelProps?: IDropdownProps): JSX.Element => {
    return (
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8, padding: 0 }}>
        <Label>{labelProps?.label}</Label>
        <TooltipHost
          content="This setting will only be taken into account when starting a new conversation."
          closeDelay={100}
          calloutProps={calloutProps}
          styles={hostStyles}
        >
          <Icon
            iconName="Info"
            aria-label="Info"
            className={styles.iconInfo}
          />
        </TooltipHost>
      </Stack>
    );
  };

  return createPortal(
    <div className={styles.settingsOverlay} onClick={() => setIsSettingsPanelOpen(false)}>
      <div className={styles.settingsPanel} data-tour="settings-panel" onClick={(e) => e.stopPropagation()}>
        <Stack
          horizontal
          className={styles.settingsPanelHeaderContainer}
          horizontalAlign="space-between"
          verticalAlign="center"
        >
          <span className={styles.settingsPanelHeader}>Settings</span>
          <DismissRegular
            role="button"
            onKeyDown={(e) =>
              e.key === " " || e.key === "Enter"
                ? setIsSettingsPanelOpen(false)
                : null
            }
            tabIndex={0}
            className={styles.settingsPanelDismiss}
            onClick={() => setIsSettingsPanelOpen(false)}
          />
        </Stack>

        <Stack
          className={styles.settingsPanelContent}
          tokens={{ childrenGap: 20, padding: 0 }}>

          {availableModelOptions.length > 0 && (
            <Dropdown
              className={styles.dropdown}
              options={availableModelOptions}
              selectedKey={selectedModel?.key}
              onChange={onSelectedModel}
              label="Preferred Model"
              onRenderLabel={onRenderPreferredAiModelLabel}
            />
          )}

          {/* Preferred Search Index hidden — managed by agent selection */}

          <Dropdown
            className={styles.dropdown}
            options={modeOptions}
            selectedKey={selectedMode?.key}
            onChange={onSelectedMode}
            label="Response Mode"
            onRenderLabel={onRenderResponseModeLabel}
          />

        </Stack>


      </div>
    </div>,
    document.body
  );
};
